import { useEffect } from 'react';
import {
  SITE_URL,
  SITE_NAME,
  DEFAULT_DESCRIPTION,
  DEFAULT_OG_IMAGE,
  TWITTER_HANDLE,
  formatTitle,
  canonicalFor
} from '../utils/seo.js';

const setMeta = (selector, attr, value) => {
  let el = document.head.querySelector(selector);
  if (!el) {
    el = document.createElement('meta');
    const [, key, val] = selector.match(/\[(\w+)="([^"]+)"\]/) || [];
    if (key && val) el.setAttribute(key, val);
    document.head.appendChild(el);
  }
  el.setAttribute(attr, value);
};

const setLink = (rel, href) => {
  let el = document.head.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', rel);
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
};

const ARTICLE_META_SELECTOR = 'meta[property^="article:"]';

const clearArticleMeta = () => {
  document.head.querySelectorAll(ARTICLE_META_SELECTOR).forEach((el) => el.remove());
};

const appendMeta = (property, content) => {
  if (!content) return;
  const el = document.createElement('meta');
  el.setAttribute('property', property);
  el.setAttribute('content', content);
  document.head.appendChild(el);
};

const setArticleMeta = (article) => {
  clearArticleMeta();
  if (!article) return;
  if (article.publishedTime) appendMeta('article:published_time', article.publishedTime);
  if (article.modifiedTime) appendMeta('article:modified_time', article.modifiedTime);
  if (article.author) appendMeta('article:author', article.author);
  if (article.section) appendMeta('article:section', article.section);
  if (Array.isArray(article.tags)) {
    article.tags.filter(Boolean).forEach((t) => appendMeta('article:tag', t));
  }
};

const JSONLD_ID = 'page-jsonld';
const BASE_JSONLD_ID = 'page-base-jsonld';

const setJsonLdEl = (id, data) => {
  let el = document.getElementById(id);
  if (data) {
    if (!el) {
      el = document.createElement('script');
      el.id = id;
      el.type = 'application/ld+json';
      document.head.appendChild(el);
    }
    el.textContent = JSON.stringify(data);
  } else if (el) {
    el.remove();
  }
};

const setJsonLd = (data) => setJsonLdEl(JSONLD_ID, data);

const titleCase = (slug) =>
  slug.split('-').map((w) => (w ? w[0].toUpperCase() + w.slice(1) : w)).join(' ');

const buildBreadcrumbs = (path) => {
  const segments = (path || '/').split('/').filter(Boolean);
  const items = [{ '@type': 'ListItem', position: 1, name: 'Home', item: SITE_URL }];
  segments.forEach((seg, idx) => {
    items.push({
      '@type': 'ListItem',
      position: idx + 2,
      name: titleCase(seg),
      item: `${SITE_URL}/${segments.slice(0, idx + 1).join('/')}`
    });
  });
  return items;
};

// True when a page's own jsonLd already declares a BreadcrumbList, so the base
// graph should not emit a second (conflicting) one. Handles a single node, an
// array of nodes, or an @graph wrapper.
const hasBreadcrumbs = (jsonLd) => {
  if (!jsonLd) return false;
  const nodes = Array.isArray(jsonLd)
    ? jsonLd
    : Array.isArray(jsonLd['@graph'])
      ? jsonLd['@graph']
      : [jsonLd];
  return nodes.some((n) => n && n['@type'] === 'BreadcrumbList');
};

const buildBaseJsonLd = ({ url, fullTitle, description, image, path, includeBreadcrumbs }) => ({
  '@context': 'https://schema.org',
  '@graph': [
    {
      '@type': 'WebPage',
      '@id': url,
      url,
      name: fullTitle,
      description,
      inLanguage: 'en-US',
      isPartOf: { '@id': `${SITE_URL}/#website` },
      primaryImageOfPage: image,
      speakable: {
        '@type': 'SpeakableSpecification',
        cssSelector: [
          '.hero__title',
          '.hero__subtitle',
          '.section-head h2',
          '.section-head p',
          '.setup__top-title',
          '.setup__top-sub',
          '.features-page__title',
          '.features-page__lede',
          '.case-studies-page__title',
          '.case-studies-page__lede',
          '.integrations-page__title',
          '.integrations-page__lede',
          '.blog-detail__title',
          '.glossary__term',
          '.glossary__definition',
          '.help-hero__lede',
          '.help-detail__title'
        ]
      }
    },
    // Only auto-generate breadcrumbs when the page hasn't supplied its own
    // (blog/feature/case-study detail pages provide richer, real-title trails).
    ...(includeBreadcrumbs
      ? [
          {
            '@type': 'BreadcrumbList',
            itemListElement: buildBreadcrumbs(path)
          }
        ]
      : [])
  ]
});

export default function useDocumentMeta({
  title,
  description = DEFAULT_DESCRIPTION,
  path,
  image = DEFAULT_OG_IMAGE,
  type = 'website',
  jsonLd,
  article,
  noindex = false
} = {}) {
  useEffect(() => {
    const fullTitle = formatTitle(title);
    const url = canonicalFor(path);

    document.title = fullTitle;
    setMeta('meta[name="description"]', 'content', description);
    setLink('canonical', url);

    // og:image / twitter:image must be absolute — several scrapers drop a
    // root-relative URL outright. Callers pass site-relative paths (e.g.
    // "/images/blog/…"), so resolve them here. `image` itself stays relative
    // for the LCP preload below, which must not point at a second origin.
    const absImage = image && image.startsWith('/') ? `${SITE_URL}${image}` : image;

    // Keep utility / dead-end pages (404, thank-you) out of the index.
    const robotsEl = document.head.querySelector('meta[name="robots"]');
    if (noindex) setMeta('meta[name="robots"]', 'content', 'noindex, nofollow');
    else if (robotsEl) robotsEl.remove();

    setMeta('meta[property="og:title"]', 'content', fullTitle);
    setMeta('meta[property="og:description"]', 'content', description);
    setMeta('meta[property="og:url"]', 'content', url);
    setMeta('meta[property="og:type"]', 'content', type);
    setMeta('meta[property="og:image"]', 'content', absImage);

    // Preload LCP image (only for local images to avoid 3rd-party DNS)
    const existingPreload = document.head.querySelector('link[rel="preload"][as="image"][data-lcp]');
    if (existingPreload) existingPreload.remove();
    if (image && image.startsWith('/')) {
      const preload = document.createElement('link');
      preload.rel = 'preload';
      preload.as = 'image';
      preload.href = image;
      preload.setAttribute('data-lcp', '');
      document.head.appendChild(preload);
    }
    setMeta('meta[property="og:site_name"]', 'content', SITE_NAME);

    setMeta('meta[name="twitter:card"]', 'content', 'summary_large_image');
    setMeta('meta[name="twitter:title"]', 'content', fullTitle);
    setMeta('meta[name="twitter:description"]', 'content', description);
    setMeta('meta[name="twitter:image"]', 'content', absImage);
    setMeta('meta[name="twitter:site"]', 'content', TWITTER_HANDLE);

    setJsonLdEl(
      BASE_JSONLD_ID,
      buildBaseJsonLd({
        url,
        fullTitle,
        description,
        image: absImage,
        path,
        includeBreadcrumbs: !hasBreadcrumbs(jsonLd)
      })
    );
    setJsonLd(jsonLd);

    if (type === 'article' && article) {
      setArticleMeta(article);
    } else {
      clearArticleMeta();
    }

    return () => {
      setJsonLd(null);
      setJsonLdEl(BASE_JSONLD_ID, null);
      clearArticleMeta();
      const r = document.head.querySelector('meta[name="robots"]');
      if (r) r.remove();
    };
  }, [title, description, path, image, type, JSON.stringify(jsonLd), JSON.stringify(article), noindex]);
}
